import {
  ActionManager,
  Color3,
  DynamicTexture,
  ExecuteCodeAction,
  Mesh,
  MeshBuilder,
  StandardMaterial,
  Vector3,
} from "@babylonjs/core";
import type {
  ShopSurface,
  SurfaceContent,
  SurfaceOptions,
  SurfacePlacement,
} from "./types.js";

/** Body lines the product-tile layout has room for between the image band and
 *  the CTA strip. Callers may pass more; the extras are not drawn. */
export const IMAGE_TOP_BODY_LINES: number = 2;

/**
 * Tier B implementation: a flat Babylon plane whose material is backed by a
 * DynamicTexture painted with vector text + an optional image. Universal —
 * works on every WebGL device.
 */
export class DynamicTextureSurface implements ShopSurface {
  readonly mesh: Mesh;
  private readonly texture: DynamicTexture;
  private readonly material: StandardMaterial;
  private readonly opts: SurfaceOptions;
  private currentContent: SurfaceContent | undefined;
  private pickHandler: (() => void) | undefined;
  private disposed: boolean = false;
  private imageEl: HTMLImageElement | undefined;
  private lastImageUrl: string | undefined;

  constructor(opts: SurfaceOptions) {
    this.opts = opts;
    this.mesh = MeshBuilder.CreatePlane(
      opts.name,
      { width: opts.widthM, height: opts.heightM },
      opts.scene
    );
    this.texture = new DynamicTexture(
      `${opts.name}-tex`,
      { width: opts.resolution.width, height: opts.resolution.height },
      opts.scene,
      false
    );
    this.material = new StandardMaterial(`${opts.name}-mat`, opts.scene);
    this.material.diffuseTexture = this.texture;
    this.material.specularColor = new Color3(0, 0, 0);
    this.material.backFaceCulling = opts.singleSided === true;
    if (opts.opacity !== undefined) {
      this.material.alpha = opts.opacity;
    }
    if (opts.emissive) {
      this.material.emissiveTexture = this.texture;
      this.material.emissiveColor = new Color3(0.4, 0.42, 0.44);
    }
    this.mesh.material = this.material;
    if (opts.excludeFromGlow === true) {
      const glow = opts.scene.effectLayers?.find((l) => l.name === "mall-glow");
      if (glow !== undefined && "addExcludedMesh" in glow) {
        (glow as { addExcludedMesh(m: Mesh): void }).addExcludedMesh(this.mesh);
      }
    }
  }

  placeAt(placement: SurfacePlacement): void {
    this.mesh.position = placement.position.clone();
    // Callers pass the yaw of the direction the surface should face (its
    // normal toward the viewer). Babylon planes render the texture readable
    // from the -Z side, so flip 180° to show the readable face that way.
    this.mesh.rotation = new Vector3(0, placement.rotationY + Math.PI, 0);
    if (placement.parent !== undefined) {
      this.mesh.parent = placement.parent;
    }
  }

  setContent(content: SurfaceContent): void {
    this.currentContent = content;
    this.repaint();
  }

  onPick(handler: () => void): void {
    this.pickHandler = handler;
    if (this.mesh.actionManager === null) {
      this.mesh.actionManager = new ActionManager(this.opts.scene);
    }
    this.mesh.actionManager.registerAction(
      new ExecuteCodeAction(ActionManager.OnPickTrigger, () => {
        this.pickHandler?.();
      })
    );
  }

  dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    if (this.imageEl !== undefined) {
      this.imageEl.onload = null;
      this.imageEl.onerror = null;
      this.imageEl = undefined;
    }
    this.mesh.dispose();
    this.material.dispose();
    this.texture.dispose();
  }

  private repaint(): void {
    if (this.currentContent === undefined) {
      return;
    }
    const ctx: CanvasRenderingContext2D | null = this.texture.getContext() as CanvasRenderingContext2D | null;
    if (ctx === null) {
      return;
    }
    const c: SurfaceContent = this.currentContent;
    const w: number = this.opts.resolution.width;
    const h: number = this.opts.resolution.height;
    const theme = this.opts.theme;

    ctx.fillStyle = theme.background;
    ctx.fillRect(0, 0, w, h);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    if (this.opts.layout === "imageTop") {
      this.repaintImageTop(ctx, c, w, h);
      this.texture.update();
      return;
    }
    if (this.opts.layout === "list") {
      this.repaintList(ctx, c, w, h);
      this.texture.update();
      return;
    }
    if (this.opts.layout === "wayfinding") {
      this.repaintWayfinding(ctx, c, w, h);
      this.texture.update();
      return;
    }

    // Default "imageLeft" layout: image (if any) in the left 40% of the surface
    let textLeft: number = 0;
    let textWidth: number = w;
    if (c.imageUrl !== undefined) {
      const imgBoxW: number = w * 0.4;
      this.drawImageInto(ctx, c.imageUrl, 0, 0, imgBoxW, h);
      textLeft = imgBoxW;
      textWidth = w - imgBoxW;
    }
    const cx: number = textLeft + textWidth / 2;
    const maxW: number = textWidth * 0.92;

    ctx.fillStyle = theme.foreground;
    const titlePx: number = this.fitFontPx(ctx, c.title, maxW, Math.round(h * 0.25), `bold {px}px ${theme.fontFamilySerif}`);
    ctx.font = `bold ${titlePx}px ${theme.fontFamilySerif}`;
    ctx.fillText(c.title, cx, h * 0.3);

    if (c.subtitle !== undefined) {
      const subPx: number = Math.round(h * 0.13);
      ctx.font = `italic ${subPx}px ${theme.fontFamilySerif}`;
      const subLines: string[] = this.wrapText(ctx, c.subtitle, maxW).slice(0, 2);
      for (let i: number = 0; i < subLines.length; i++) {
        ctx.fillText(subLines[i], cx, h * 0.52 + i * subPx * 1.2);
      }
    }

    if (c.bodyLines !== undefined && c.bodyLines.length > 0) {
      ctx.fillStyle = theme.accent;
      const bodyPx: number = Math.round(h * 0.08);
      ctx.font = `${bodyPx}px ${theme.fontFamily}`;
      const wrapped: string[] = c.bodyLines
        .flatMap((line: string) => this.wrapText(ctx, line, maxW))
        .slice(0, 3);
      const lineH: number = bodyPx * 1.25;
      // The CTA box paints over the bottom h*0.18 afterward; when present,
      // pull the block up just enough for the last line to clear it, and no
      // further, so the body never crowds the subtitle above it.
      let startY: number = h * 0.77;
      if (c.ctaText !== undefined) {
        // 0.85 not 0.6: half the glyph height clears the box, the rest is the
        // breathing room that keeps the last line off the CTA edge.
        const lastLineMaxY: number = h - h * 0.18 - bodyPx * 0.85;
        startY = Math.min(startY, lastLineMaxY - (wrapped.length - 1) * lineH);
      }
      for (let i: number = 0; i < wrapped.length; i++) {
        ctx.fillText(wrapped[i], cx, startY + i * lineH);
      }
    }

    if (c.ctaText !== undefined) {
      const boxH: number = h * 0.18;
      this.drawCtaBox(ctx, c.ctaText, cx, h - boxH, w * 0.4, boxH, w * 0.95);
    }

    this.texture.update();
  }

  /** White CTA strip with the label in background color. The text is
   *  invisible wherever it leaves the box, so the box grows to the text
   *  (up to maxBoxW) and the font shrinks if it still doesn't fit. */
  private drawCtaBox(
    ctx: CanvasRenderingContext2D,
    text: string,
    cx: number,
    boxY: number,
    minBoxW: number,
    boxH: number,
    maxBoxW: number
  ): void {
    const theme = this.opts.theme;
    const basePx: number = Math.round(boxH * 0.55);
    const px: number = this.fitFontPx(ctx, text, maxBoxW - boxH, basePx, `bold {px}px ${theme.fontFamily}`);
    ctx.font = `bold ${px}px ${theme.fontFamily}`;
    const boxW: number = Math.min(
      Math.max(minBoxW, ctx.measureText(text).width + boxH),
      maxBoxW
    );
    ctx.fillStyle = theme.foreground;
    ctx.fillRect(cx - boxW / 2, boxY, boxW, boxH);
    ctx.fillStyle = theme.background;
    ctx.fillText(text, cx, boxY + boxH / 2);
  }

  /** Wayfinding signage: dark panel with a hairline inset border, tracked
   *  uppercase sans title, a thin accent rule, and a muted subtitle. The
   *  letter tracking uses canvas letterSpacing where the browser has it and
   *  degrades to plain text elsewhere (jsdom, older Safari). */
  private repaintWayfinding(
    ctx: CanvasRenderingContext2D,
    c: SurfaceContent,
    w: number,
    h: number
  ): void {
    const theme = this.opts.theme;
    ctx.fillStyle = theme.background;
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = "rgba(255, 255, 255, 0.16)";
    ctx.lineWidth = 2;
    ctx.strokeRect(4, 4, w - 8, h - 8);

    const cx: number = w / 2;
    const title: string = c.title.toUpperCase();
    const hasSub: boolean = c.subtitle !== undefined;
    const tracked: CanvasRenderingContext2D & { letterSpacing?: string } = ctx;
    const canTrack: boolean = typeof tracked.letterSpacing === "string";

    ctx.fillStyle = theme.foreground;
    if (canTrack) {
      tracked.letterSpacing = `${Math.max(2, Math.round(h * 0.04))}px`;
    }
    const titlePx: number = this.fitFontPx(
      ctx, title, w * 0.86, Math.round(h * (hasSub ? 0.32 : 0.38)), `600 {px}px ${theme.fontFamily}`
    );
    ctx.font = `600 ${titlePx}px ${theme.fontFamily}`;
    ctx.fillText(title, cx, hasSub ? h * 0.38 : h * 0.5);
    if (canTrack) {
      tracked.letterSpacing = "0px";
    }

    const ruleW: number = Math.min(w * 0.22, 150);
    const ruleY: number = hasSub ? h * 0.58 : h * 0.72;
    ctx.fillStyle = theme.accent;
    ctx.fillRect(cx - ruleW / 2, ruleY - 2, ruleW, 4);

    if (c.subtitle !== undefined) {
      ctx.fillStyle = "rgba(255, 255, 255, 0.62)";
      const subPx: number = this.fitFontPx(
        ctx, c.subtitle, w * 0.9, Math.round(h * 0.15), `{px}px ${theme.fontFamily}`
      );
      ctx.font = `${subPx}px ${theme.fontFamily}`;
      ctx.fillText(c.subtitle, cx, h * 0.78);
    }
  }

  /** Directory/menu layout: header band with the title, then body lines
   *  evenly spaced and sized to fit however many there are. Lines beginning
   *  with "— " render as accented section headings. */
  private repaintList(
    ctx: CanvasRenderingContext2D,
    c: SurfaceContent,
    w: number,
    h: number
  ): void {
    const theme = this.opts.theme;
    const headerH: number = h * 0.14;
    ctx.fillStyle = theme.accent;
    ctx.fillRect(0, 0, w, headerH);
    ctx.fillStyle = theme.background;
    ctx.font = `bold ${Math.round(headerH * 0.5)}px ${theme.fontFamilySerif}`;
    ctx.fillText(c.title, w / 2, headerH / 2);

    const lines: string[] = c.bodyLines ?? [];
    if (lines.length === 0) {
      return;
    }
    const top: number = headerH + h * 0.03;
    const lineH: number = Math.min(h * 0.075, (h - top - h * 0.03) / lines.length);
    const fontPx: number = Math.round(lineH * 0.62);
    for (let i: number = 0; i < lines.length; i++) {
      const y: number = top + lineH * (i + 0.5);
      const line: string = lines[i];
      if (line.startsWith("— ")) {
        const heading: string = line.slice(2).toUpperCase();
        ctx.fillStyle = theme.accent;
        const px: number = this.fitFontPx(ctx, heading, w * 0.92, fontPx, `bold {px}px ${theme.fontFamily}`);
        ctx.font = `bold ${px}px ${theme.fontFamily}`;
        ctx.fillText(heading, w / 2, y);
      } else {
        ctx.fillStyle = theme.foreground;
        const px: number = this.fitFontPx(ctx, line, w * 0.92, fontPx, `{px}px ${theme.fontFamily}`);
        ctx.font = `${px}px ${theme.fontFamily}`;
        ctx.fillText(line, w / 2, y);
      }
    }
  }

  /** Product-tile layout: photo fills the top ~66% edge to edge, then title,
   *  up to TWO body lines (price, then the sold count or the stock notice),
   *  and a small CTA strip along the bottom. */
  private repaintImageTop(
    ctx: CanvasRenderingContext2D,
    c: SurfaceContent,
    w: number,
    h: number
  ): void {
    const theme = this.opts.theme;
    // 0.66 rather than 0.7: the second body line needs the band. Callers have
    // been passing a second line all along (tiles set "Out of stock",
    // holograms set "Bought X ago") and it was silently dropped.
    const imgH: number = h * 0.66;
    if (c.imageUrl !== undefined) {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, w, imgH);
      this.drawImageInto(ctx, c.imageUrl, 0, 0, w, imgH);
    }
    const cx: number = w / 2;

    ctx.fillStyle = theme.foreground;
    const titlePx: number = this.fitFontPx(ctx, c.title, w * 0.94, Math.round(h * 0.055), `bold {px}px ${theme.fontFamily}`);
    ctx.font = `bold ${titlePx}px ${theme.fontFamily}`;
    ctx.fillText(c.title, cx, imgH + h * 0.055);

    if (c.bodyLines !== undefined && c.bodyLines.length > 0) {
      const count: number = Math.min(c.bodyLines.length, IMAGE_TOP_BODY_LINES);
      // First line carries the price at full weight; the second is a quieter
      // qualifier, so it renders smaller and in the foreground colour.
      for (let i: number = 0; i < count; i++) {
        const line: string = c.bodyLines[i];
        const px: number = Math.round(h * (i === 0 ? 0.058 : 0.042));
        ctx.fillStyle = i === 0 ? theme.accent : theme.foreground;
        const fitted: number = this.fitFontPx(ctx, line, w * 0.92, px, `bold {px}px ${theme.fontFamily}`);
        ctx.font = `bold ${fitted}px ${theme.fontFamily}`;
        ctx.fillText(line, cx, imgH + h * (0.128 + i * 0.068));
      }
    }

    if (c.ctaText !== undefined) {
      const boxH: number = h * 0.09;
      // Sits lower than it used to so the sold line above it has a real gap
      // instead of resting on the box edge.
      this.drawCtaBox(ctx, c.ctaText, cx, h - boxH * 1.15, w * 0.44, boxH, w * 0.95);
    }
  }

  /** Largest font size (starting at basePx, floored at 40% of it) at which
   *  `text` fits within maxWidth. `fontTemplate` uses "{px}" for the size. */
  private fitFontPx(
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
    basePx: number,
    fontTemplate: string
  ): number {
    let px: number = basePx;
    const minPx: number = Math.max(8, Math.round(basePx * 0.4));
    while (px > minPx) {
      ctx.font = fontTemplate.replace("{px}", String(px));
      if (ctx.measureText(text).width <= maxWidth) {
        break;
      }
      px -= 2;
    }
    return px;
  }

  /** Word-wrap `text` to lines no wider than maxWidth at the current ctx font. */
  private wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
    const words: string[] = text.split(/\s+/).filter((word: string) => word.length > 0);
    const lines: string[] = [];
    let current: string = "";
    for (const word of words) {
      const candidate: string = current.length === 0 ? word : `${current} ${word}`;
      if (ctx.measureText(candidate).width <= maxWidth || current.length === 0) {
        current = candidate;
      } else {
        lines.push(current);
        current = word;
      }
    }
    if (current.length > 0) {
      lines.push(current);
    }
    return lines;
  }

  private drawImageInto(
    ctx: CanvasRenderingContext2D,
    url: string,
    x: number,
    y: number,
    w: number,
    h: number
  ): void {
    if (this.lastImageUrl !== url) {
      this.lastImageUrl = url;
      if (this.imageEl !== undefined) {
        this.imageEl.onload = null;
        this.imageEl.onerror = null;
      }
      const img: HTMLImageElement = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        if (!this.disposed && this.currentContent !== undefined) {
          this.repaint();
        }
      };
      img.onerror = () => {
        // Surface stays text-only on image failure (no fallback URL).
      };
      img.src = url;
      this.imageEl = img;
    }
    if (this.imageEl !== undefined && this.imageEl.complete && this.imageEl.naturalWidth > 0) {
      const iw: number = this.imageEl.naturalWidth;
      const ih: number = this.imageEl.naturalHeight;
      const scale: number = Math.min(w / iw, h / ih);
      const dw: number = iw * scale;
      const dh: number = ih * scale;
      const dx: number = x + (w - dw) / 2;
      const dy: number = y + (h - dh) / 2;
      ctx.drawImage(this.imageEl, dx, dy, dw, dh);
    }
  }
}
